<template>
  <div ref="containerRef">
    <adjustments-account-select
      v-if="inputMode === 'select'"
      v-model="selectedAccount"
      :focused="props.focused"
      :error="error"
      @create-new-account="handleCreateNewAccount"
      @selected="emit('filled')"
    ></adjustments-account-select>
    <adjustments-account-create
      v-if="inputMode === 'create'"
      v-model="selectedAccount"
      :error="error"
    ></adjustments-account-create>
    <adjustments-account-filled
      v-if="inputMode === 'filled'"
      :account="selectedAccount"
      :disabled="disabled"
      @edit-selected-account="handleEditSelectedAccount"
    >
    </adjustments-account-filled>
  </div>
</template>

<script setup lang="ts">
import {
  SubAdjustmentRowAccount,
  SubAdjustmentRowAccountNew,
} from '@app/application/adjustments/types'

import { onClickOutside, useVModel } from '@vueuse/core'
import { computed, ref, watch } from 'vue'

import AdjustmentsAccountSelect from './adjustments-account-select.vue'
import AdjustmentsAccountFilled from './adjustments-account-filled.vue'
import AdjustmentsAccountCreate from './adjustments-account-create.vue'

// eslint-disable-next-line no-undef
defineOptions({
  name: 'AdjustmentsAccountSelector',
})

const editedAccount = ref<boolean>(false)

interface Props {
  disabled?: boolean
  focused?: number
  error?: string
  pinnedState?: 'create' | 'select' | 'filled'
  modelValue?: SubAdjustmentRowAccount
}

const props = withDefaults(defineProps<Props>(), {
  error: '',
  focused: 0,
  pinnedState: undefined,
  modelValue: () => ({} as SubAdjustmentRowAccount),
})

const containerRef = ref<HTMLElement | null>(null)

onClickOutside(containerRef, () => {
  if (editedAccount.value) {
    editedAccount.value = false
  }
})

const emit =
  defineEmits<{
    filled: []
    focused: []
    'update:modelValue': [value: SubAdjustmentRowAccount]
  }>()

const selectedAccount = useVModel(props, 'modelValue', emit, {
  passive: true,
  deep: true,
})

watch(selectedAccount, () => {
  editedAccount.value = false
})

const handleEditSelectedAccount = (value: boolean) => {
  editedAccount.value = value
  emit('focused')
}

const handleCreateNewAccount = (account: SubAdjustmentRowAccountNew) => {
  selectedAccount.value = account
}

const inputMode = computed(() => {
  const hasAnEmptyAccount = Object.values(selectedAccount.value ?? {}).every(
    (value) => !value
  )

  if (props.disabled) {
    return 'filled'
  }

  if (!selectedAccount.value || editedAccount.value || hasAnEmptyAccount) {
    return 'select'
  } else if (selectedAccount.value?.id === null) {
    return 'create'
  } else {
    return 'filled'
  }
})
</script>
